import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { SourceFieldOption } from '@/api/campaigns/types';
import type { TargetOption } from './valueMapAutoFill';

const UNMATCHED_VALUE = '__leave_unmatched__';

export interface ValueMapTargetGroup {
    target: string;
    targetLabel: string;
    sourceOptions: SourceFieldOption[];
    targetOptions: TargetOption[];
}

export interface ValueMapPanelProps {
    groups: ValueMapTargetGroup[];
    valueMaps: Record<string, Record<string, string>>;
    onChange: (target: string, sourceValueKey: string, targetOptionKey: string | null) => void;
}

/**
 * Value-map panel for enum/select targets (field-mapping.md §4.3): one row
 * per known source value, each picking the target option it should map to.
 * An unmatched value is left empty on purpose — at runtime it's appended to
 * notes and the submission is flagged with a warning, the lead still created.
 */
const ValueMapPanel: React.FC<ValueMapPanelProps> = ({ groups, valueMaps, onChange }) => {
    const { t } = useTranslation('crm');

    if (groups.length === 0) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">
                    {t('campaigns.editor.mapping.valueMapTitle')}
                </CardTitle>
                <p className="text-sm text-muted-foreground">
                    {t('campaigns.editor.mapping.valueMapDescription')}
                </p>
            </CardHeader>
            <CardContent className="space-y-5">
                {groups.map(group => (
                    <div key={group.target}>
                        <p className="mb-2 text-sm font-medium">{group.targetLabel}</p>
                        <div className="space-y-2">
                            {group.sourceOptions.map(option => {
                                const current =
                                    valueMaps[group.target]?.[option.key] ?? UNMATCHED_VALUE;
                                return (
                                    <div
                                        key={option.key}
                                        className="grid grid-cols-[1fr_auto_1fr] items-center gap-3"
                                    >
                                        <span className="truncate text-sm" title={option.label}>
                                            {option.label}
                                        </span>
                                        <span className="text-xs text-muted-foreground">
                                            {t('campaigns.editor.mapping.valueMapArrow')}
                                        </span>
                                        <Select
                                            value={current}
                                            onValueChange={value =>
                                                onChange(
                                                    group.target,
                                                    option.key,
                                                    value === UNMATCHED_VALUE ? null : value
                                                )
                                            }
                                        >
                                            <SelectTrigger className="h-8 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value={UNMATCHED_VALUE}>
                                                    {t(
                                                        'campaigns.editor.mapping.valueMapUnmatched'
                                                    )}
                                                </SelectItem>
                                                {group.targetOptions.map(targetOption => (
                                                    <SelectItem
                                                        key={targetOption.key}
                                                        value={targetOption.key}
                                                    >
                                                        {targetOption.label}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </CardContent>
        </Card>
    );
};

export default ValueMapPanel;
